/* ============================================================================
   nuud US — buy box
   ----------------------------------------------------------------------------
   Drives pack selection, subscribe vs one-time, delivery frequency, the price
   line and the gallery's pack-specific slot.

   It reads everything from the JSON that Liquid renders into
   <script type="application/json" data-buy-data>. This file therefore contains
   no prices, no product names and no Shopify knowledge: change the store, and
   this keeps working.

   Multiple buy boxes on one page are supported; each is scoped to its own root.
   ========================================================================= */
(function () {
  'use strict';

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || window.Shopify && window.Shopify.currency && window.Shopify.currency.active || 'USD'
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* ------------------------------------------------------- welcome discount
     A Shopify discount lives on the CART, so every price rendered here is the
     list price and a visitor who arrived from a "10% off" email sees no sign of
     it. `cart.discount_codes` tells us a code is waiting but never what it is
     worth, so the percentage comes from theme settings and only codes matching
     the configured prefix are trusted. Anything else leaves prices alone.

     `pending` exists because /cart.js is async: a box can finish rendering
     before we know, so we re-render when the answer lands. */
  var welcome = { percent: 0, known: false };

  /* Shopify FLOORS the discount rather than rounding the net: 2879 at 10% is a
     287 discount, not 288, so the customer pays 2592 and not 2591. Match it, or
     the page and the checkout disagree by a cent. */
  function offer(cents) {
    if (!welcome.percent) return cents;
    return cents - Math.floor(cents * welcome.percent / 100);
  }

  function priceHTML(cents, money) {
    if (!welcome.percent) return money(cents);
    return '<s>' + money(cents) + '</s> ' + money(offer(cents));
  }

  /* --------------------------------------------------------- plan cadence
     How many days a selling plan waits between deliveries.

     Recharge ships a machine-readable cadence in a plan option ("3-month");
     the display name ("Delivery every 3 months") is the fallback. Both are
     read, options first, so a renamed plan does not change the maths. Returns
     0 when nothing parses, which the callers treat as "unknown" rather than
     as "zero days". */
  var UNIT = { day: 1, week: 7, month: 30.44, year: 365 };

  function planDays(plan) {
    var sources = (plan.options || []).slice();
    sources.push(plan.name || '');
    for (var i = 0; i < sources.length; i++) {
      var m = String(sources[i]).toLowerCase().match(/(\d+)\s*-?\s*(day|week|month|year)s?\b/);
      if (m) return parseInt(m[1], 10) * UNIT[m[2]];
    }
    return 0;
  }

  /* What the frequency select shows for a plan.

     Recharge's German plan names are whole sentences ending in a full stop
     ("Eine Packung alle 4 Monate mit 10 % Rabatt."), which reads badly in a
     dropdown and worse in the price line, where the English build lowercases
     the name and German nouns must keep their capitals. So the label is built
     from the interval we already parse rather than from the name. Falls back
     to the raw name if the interval cannot be read. */
  function planLabel(plan) {
    var d = planDays(plan);
    if (!d) return plan.name;
    if (d < 28) {
      var w = Math.round(d / 7);
      return w === 1 ? 'Chaque semaine' : 'Toutes les ' + w + ' semaines';
    }
    var m = Math.round(d / 30.44);
    return m === 1 ? 'Chaque mois' : 'Tous les ' + m + ' mois';
  }

  /* Which cadence opens selected.

     Shopify returns the plans in the order the app created them, and taking
     the first one is how the Family Pack came to default to a delivery every
     2 months for a pack that lasts about nine months. Pick the plan whose
     interval sits closest to how long the pack actually lasts, and on a tie
     take the slower one — arriving slightly early is a surplus that builds
     every cycle, and a surplus is what people cancel over.

     Falls back to the first plan when no interval can be read or the pack has
     no `days`, which is exactly the old behaviour. */
  function defaultPlan(pack) {
    if (!pack.plans.length) return null;
    if (!pack.days) return pack.plans[0];

    var best = null, bestGap = Infinity, bestDays = 0;
    pack.plans.forEach(function (plan) {
      var days = planDays(plan);
      if (!days) return;
      var gap = Math.abs(days - pack.days);
      if (gap < bestGap || (gap === bestGap && days > bestDays)) {
        best = plan; bestGap = gap; bestDays = days;
      }
    });
    return best || pack.plans[0];
  }

  function init(root) {
    var dataEl = root.parentNode.querySelector('[data-buy-data]') ||
                 document.querySelector('[data-buy-data]');
    if (!dataEl) return;

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!data.packs || !data.packs.length) return;

    var els = {
      packs:      Array.prototype.slice.call(root.querySelectorAll('[data-pack]')),
      modes:      Array.prototype.slice.call(root.querySelectorAll('[data-mode]')),
      modeBlock:  root.querySelector('[data-mode-block]'),
      modeCards:  Array.prototype.slice.call(root.querySelectorAll('[data-mode-card]')),
      freq:       root.querySelector('[data-frequency]'),
      oncePrice:  root.querySelector('[data-onetime-price]'),
      hint:       root.querySelector('[data-supply-hint]'),
      price:      root.querySelector('[data-price]'),
      priceMeta:  root.querySelector('[data-price-meta]'),
      submit:     root.querySelector('[data-submit]'),
      variant:    root.querySelector('[data-variant-input]'),
      plan:       root.querySelector('[data-plan-input]'),
      saving:     root.querySelector('[data-subscribe-saving]'),
      galleryImg: root.querySelector('[data-gallery-main]'),
      thumbImg:   root.querySelector('[data-gallery-thumb-main]')
    };

    var startIndex = typeof data.defaultIndex === 'number' &&
                     data.packs[data.defaultIndex] ? data.defaultIndex : 0;

    var firstPlan = defaultPlan(data.packs[startIndex]);

    var state = {
      pack: startIndex,
      // Default to subscribe only when the selected pack actually has plans.
      mode: firstPlan ? 'subscribe' : 'onetime',
      planId: firstPlan ? firstPlan.id : null
    };

    function currentPack() { return data.packs[state.pack]; }

    function currentPlan() { return planFor(currentPack()); }

    /* The plan `pack` is on: the chosen one when that pack offers it, else the
       one that matches how long the pack lasts. Every pack in the ladder needs
       this, not just the selected one, because the ladder is priced at the
       current mode. */
    function planFor(pack) {
      for (var i = 0; i < pack.plans.length; i++) {
        if (pack.plans[i].id === state.planId) return pack.plans[i];
      }
      return defaultPlan(pack);
    }

    /* ------------------------------------------------------------ rendering */
    function renderPacks() {
      els.packs.forEach(function (btn, i) {
        var on = i === state.pack;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');

        /* The ladder prices at whatever mode is selected.

           It used to always show the one-time price, so with Subscribe chosen
           the page carried four figures for one product: the pack card's
           $35.99 struck to $32.40, and the price line's $32.39 struck to
           $29.16. Two of those were prices nobody was about to pay. Pricing
           the ladder at the selected mode leaves one number in two places,
           and the one-time row underneath is where the un-subscribed price
           now lives.

           Comparing packs still works: every pack takes the same subscription
           discount, so the ranking between them is unchanged. */
        var pk = data.packs[i];
        if (!pk) return;

        var planForPack = state.mode === 'subscribe' ? planFor(pk) : null;
        var list = planForPack ? planForPack.price : pk.price;

        var priceEl = btn.querySelector('[data-pack-price]');
        if (priceEl) priceEl.innerHTML = priceHTML(list, money);

        var unitEl = btn.querySelector('[data-pack-unit]');
        if (unitEl && pk.days && pk.tubes) {
          var net = offer(list);
          unitEl.textContent = money(Math.round(net / pk.days)) + ' par jour \u00b7 ' +
                               money(Math.round(net / pk.tubes)) + ' par tube';
        }
      });
    }

    function renderModes() {
      var pack = currentPack();
      var hasPlans = pack.plans.length > 0;

      // A pack with no selling plan cannot be subscribed to, so the whole
      // subscribe/frequency apparatus is hidden rather than shown broken.
      if (els.modeBlock) els.modeBlock.hidden = !hasPlans;
      if (!hasPlans && state.mode === 'subscribe') state.mode = 'onetime';

      els.modes.forEach(function (btn) {
        var on = btn.getAttribute('data-mode') === state.mode;
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
      });

      // The card carries the selected styling, not the button inside it, so
      // that the whole option lights up and its body can open.
      els.modeCards.forEach(function (card) {
        card.classList.toggle('is-selected',
          card.getAttribute('data-mode-card') === state.mode);
      });

      /* The row shows the list one-time price while Subscribe is selected —
         that is the comparison the visitor is making. Once one-time IS the
         selected mode it has to show what they will actually pay, or the row
         and the big price line disagree by the welcome discount. */
      if (els.oncePrice) {
        els.oncePrice.innerHTML = state.mode === 'onetime'
          ? priceHTML(pack.price, money)
          : money(pack.price);
      }

      if (els.saving && hasPlans) {
        var plan = currentPlan();
        var perDelivery = pack.price - (plan ? plan.price : pack.price);
        var days = plan ? planDays(plan) : 0;

        /* State the saving in money, and annualise it.

           "Save 10%" is the weakest form, a per-delivery figure beats it, and
           the yearly total beats that — it is the largest number that is still
           true, and it is the one the visitor is actually deciding about. Both
           are shown because the per-delivery figure is what appears on the
           card they are looking at and the year is what it adds up to.

           Falls back to the per-delivery line alone when the cadence cannot be
           read, rather than inventing a year. */
        if (perDelivery <= 0) {
          els.saving.textContent = '';
        } else if (days > 0) {
          var perYear = Math.round(perDelivery * 365 / days);
          els.saving.textContent = 'Économise ' + money(perYear) + ' par an, ' +
                                   money(perDelivery) + ' par livraison';
        } else {
          els.saving.textContent = 'Économise ' + money(perDelivery) + ' par livraison';
        }
      }
    }

    function renderFrequency() {
      if (!els.freq) return;
      var pack = currentPack();
      els.freq.innerHTML = '';
      pack.plans.forEach(function (plan) {
        var opt = document.createElement('option');
        opt.value = String(plan.id);
        opt.textContent = planLabel(plan);
        if (plan.id === state.planId) opt.selected = true;
        els.freq.appendChild(opt);
      });
      /* Say how long the pack lasts AND how that lines up with the cadence
         on screen. The hint used to state the supply on its own, directly
         under a select that defaulted to a faster cadence than the supply —
         so the page told you a pack lasts 19-20 weeks and then offered to
         send the next one at 13.

         `slack` is positive when the pack outlasts the gap between
         deliveries, which means the next one turns up while there is still
         cream left and a surplus builds every cycle; negative means it runs
         out before the next one lands. The tolerance scales with the pack,
         because a fortnight either way is nothing on a nine-month supply and
         a third of a Starter Pack.

         This only ever describes the cadence on screen. It never suggests a
         different one: the default is already the closest match the plans
         allow, so advice to pick another would be advice to pick a worse fit.
         When every option ships faster than the pack is used — the Family
         Pack lasts about nine months and Recharge stops at four — the honest
         line is that a spare builds up, and that is a Recharge problem, not
         something copy can fix. */
      if (els.hint) {
        var parts = [];
        if (pack.duration) parts.push(pack.contents + ', te dure ' + pack.duration);

        var plan = currentPlan();
        var days = plan ? planDays(plan) : 0;
        if (days && pack.days) {
          var slack = pack.days - days;
          var band  = Math.max(10, pack.days * 0.15);

          if (slack > band)       parts.push('c’est plus rapide que ce que tu utilises, donc il va t’en rester');
          else if (slack < -band) parts.push('tu risques d’être à court avant la prochaine');
          else                    parts.push('la prochaine arrive à peu près quand celle-ci est finie');
        }

        // Sentence case on every clause, since they are joined into sentences.
        els.hint.textContent = parts.length
          ? parts.map(function (t) { return t.charAt(0).toUpperCase() + t.slice(1); }).join('. ') + '.'
          : '';
      }
    }

    function renderPrice() {
      var pack = currentPack();
      var plan = state.mode === 'subscribe' ? currentPlan() : null;
      var pay  = plan ? plan.price : pack.price;

      if (els.price) els.price.innerHTML = priceHTML(pay, money);

      /* Just the cadence. The one-time price moved into the one-time row,
         where it is the whole point of that row. */
      if (els.priceMeta) {
        var lab = plan ? planLabel(plan) : '';
        els.priceMeta.textContent = lab ? lab.charAt(0).toLowerCase() + lab.slice(1) : '';
      }

      if (els.submit) {
        /* No price on the button. It sat about 40px under the same figure set
           at 40px, so it was the second of two identical numbers rather than
           new information. The big price stays because the pack card above it
           still shows the ONE-TIME price while Subscribe is selected, which
           makes the big figure the only place the payable amount is primary. */
        els.submit.textContent = !pack.available
          ? data.labels.soldOut
          : (plan ? data.labels.ctaSubscribe : data.labels.ctaOnetime);
        els.submit.disabled = !pack.available;
      }

      if (els.variant) els.variant.value = pack.id;
      if (els.plan)    els.plan.value    = plan ? plan.id : '';
    }

    // Liquid renders the pack shot through `image_tag`, which emits a srcset.
    // A srcset BEATS src: assigning .src alone updates the attribute and
    // changes nothing on screen. The srcset and sizes have to go first.
    function swapImage(img, url, alt) {
      if (!img || !url) return;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
      if (typeof alt === 'string') img.alt = alt;
    }

    function renderGallery() {
      var pack = currentPack();
      swapImage(els.galleryImg, pack.image, pack.name);
      swapImage(els.thumbImg, pack.image, '');
      // Changing pack returns the stage to slot 0, so the shot you just chose
      // is the one you see rather than whichever slide was open.
      var gallery = root.querySelector('[data-gallery]');
      if (gallery) {
        gallery.dispatchEvent(new CustomEvent('gallery:reset', { bubbles: true }));
      }
    }

    function render() {
      renderPacks();
      renderModes();
      renderFrequency();
      renderPrice();
      renderGallery();
      var detail = { pack: currentPack(), mode: state.mode };
      // Park the state on the element as well as announcing it. Anything that
      // boots after this file (the sticky bar does: both scripts are deferred,
      // so this one runs first and its first `buybox:change` is gone before the
      // bar is listening) can read the current selection instead of waiting for
      // an event that has already fired.
      root.buyboxState = detail;
      root.dispatchEvent(new CustomEvent('buybox:change', {
        bubbles: true,
        detail: detail
      }));
    }

    /* --------------------------------------------------------------- events */
    els.packs.forEach(function (btn, i) {
      btn.addEventListener('click', function () {
        state.pack = i;
        var pack = currentPack();
        // Keep the chosen cadence if the new pack offers it. Otherwise fall
        // back to the one that matches the new pack's supply, not to whichever
        // plan the app happened to create first.
        var keep = pack.plans.some(function (p) { return p.id === state.planId; });
        if (!keep) {
          var d = defaultPlan(pack);
          state.planId = d ? d.id : null;
        }
        render();
      });
    });

    els.modes.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.mode = btn.getAttribute('data-mode');
        render();
      });
    });

    if (els.freq) {
      els.freq.addEventListener('change', function () {
        state.planId = parseInt(els.freq.value, 10);
        render();
      });
    }

    rerender.push(render);
    render();
    askCart(data.welcome);
  }

  var rerender = [];

  function askCart(cfg) {
    if (welcome.known || !cfg || !cfg.show || !cfg.percent || !cfg.prefix) return;
    welcome.known = true;

    var wanted = String(cfg.prefix).toUpperCase().replace(/_/g, '-');

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var match = (cart.discount_codes || []).some(function (c) {
          return String(c.code || '').toUpperCase().replace(/_/g, '-').indexOf(wanted) === 0;
        });
        if (!match) return;
        welcome.percent = cfg.percent;
        rerender.forEach(function (fn) { fn(); });
      })
      .catch(function () { /* leave prices alone rather than guess */ });
  }

  function boot() {
    document.querySelectorAll('[data-buy-box]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-initialise when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
